import { useState, useEffect, useCallback } from "react";
import {
	Page,
	Layout,
	Card,
	Text,
	Button,
	Badge,
	BlockStack,
	InlineGrid,
	Icon,
	Divider,
	Box,
	InlineStack,
	ProgressBar,
	SkeletonBodyText,
	SkeletonDisplayText,
} from "@shopify/polaris";
import { CheckIcon } from "@shopify/polaris-icons";
import { authenticatedFetch, getShopDomain } from "../api";

const APP_HANDLE = "duxly-translate-serverless";

interface PlanTier {
	name: string;
	price: string;
	features: string[];
	recommended?: boolean;
}

interface UsageData {
	productsTranslated: number;
	productLimit: number;
	planName: string;
}

const PLANS: PlanTier[] = [
	{
		name: "Starter",
		price: "$9",
		features: [
			"Up to 100 products / month",
			"2 target languages",
			"Auto-translate hourly during business hours",
			"DeepL translation engine",
			"Title & description fields",
			"10 glossary terms",
		],
	},
	{
		name: "Growth",
		price: "$29",
		recommended: true,
		features: [
			"Up to 250 products / month",
			"5 target languages",
			"Auto-translate hourly during business hours",
			"DeepL or OpenAI",
			"All fields including metafields",
			"50 glossary terms",
			"Email support",
		],
	},
	{
		name: "Enterprise",
		price: "$79",
		features: [
			"Up to 500 products / month",
			"Unlimited target languages",
			"Auto-translate hourly during business hours",
			"DeepL or OpenAI",
			"All fields including metafields",
			"Unlimited glossary terms",
			"Priority email support",
		],
	},
];

function UsageBar({ usage }: { usage: UsageData }) {
	// Hide the bar entirely when there is no active plan / zero limit — otherwise
	// we'd render "0 of 0 translated" and NaN% progress on every fresh install.
	if (!usage.planName || usage.productLimit <= 0) {
		return null;
	}
	const percentage = Math.min(
		Math.round((usage.productsTranslated / usage.productLimit) * 100),
		100,
	);
	const remaining = Math.max(usage.productLimit - usage.productsTranslated, 0);
	const progressTone = percentage >= 90 ? "critical" : "highlight";
	const badgeTone = percentage >= 90 ? "critical" : percentage >= 70 ? "warning" : "info";

	return (
		<Card>
			<BlockStack gap="300">
				<InlineStack align="space-between" blockAlign="center">
					<Text as="h2" variant="headingMd">
						Monthly usage
					</Text>
					<Badge tone={badgeTone}>
						{`${usage.planName} plan`}
					</Badge>
				</InlineStack>
				<ProgressBar progress={percentage} tone={progressTone} size="small" />
				<InlineStack align="space-between">
					<Text as="p" variant="bodySm" tone="subdued">
						{usage.productsTranslated} of {usage.productLimit} products translated
					</Text>
					<Text as="p" variant="bodySm" tone="subdued">
						{remaining} remaining
					</Text>
				</InlineStack>
			</BlockStack>
		</Card>
	);
}

function PlanCard({
	plan,
	currentPlan,
}: {
	plan: PlanTier;
	currentPlan: string;
}) {
	const isCurrent = plan.name === currentPlan;

	return (
		<Card>
			<BlockStack gap="500">
				<BlockStack gap="200">
					<InlineStack align="space-between" blockAlign="center">
						<Text as="h2" variant="headingLg">
							{plan.name}
						</Text>
						<InlineStack gap="200">
							{isCurrent && <Badge tone="success">Current plan</Badge>}
							{!isCurrent && <Badge tone="read-only">Inactive</Badge>}
							{plan.recommended && !isCurrent && <Badge tone="info">Most popular</Badge>}
						</InlineStack>
					</InlineStack>
					<InlineStack blockAlign="baseline" gap="100">
						<Text as="p" variant="heading2xl">
							{plan.price}
						</Text>
						<Text as="p" variant="bodyMd" tone="subdued">
							/ month
						</Text>
					</InlineStack>
				</BlockStack>

				<Divider />

				<BlockStack gap="300">
					<Text as="h3" variant="headingSm">
						What's included
					</Text>
					<BlockStack gap="200">
						{plan.features.map((feature) => (
							<InlineStack key={feature} gap="200" blockAlign="start" wrap={false}>
								<Box>
									<Icon source={CheckIcon} tone="success" />
								</Box>
								<Text as="span" variant="bodyMd">
									{feature}
								</Text>
							</InlineStack>
						))}
					</BlockStack>
				</BlockStack>
			</BlockStack>
		</Card>
	);
}

function PlanManagementCard({
	usage,
	planSelectionUrl,
}: {
	usage: UsageData | null;
	planSelectionUrl: string | null;
}) {
	const hasActivePlan = Boolean(usage?.planName);
	const activePlan = hasActivePlan ? `${usage!.planName} plan` : "No active plan";
	const actionLabel = hasActivePlan ? "Change plan in Shopify" : "Select plan in Shopify";

	return (
		<Card>
			<InlineStack align="space-between" blockAlign="center" gap="400">
				<BlockStack gap="100">
					<Text as="h2" variant="headingMd">
						Current plan
					</Text>
					<InlineStack gap="200" blockAlign="center">
						<Text as="p" variant="headingLg">
							{activePlan}
						</Text>
						{hasActivePlan ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Inactive</Badge>}
					</InlineStack>
					<Text as="p" variant="bodyMd" tone="subdued">
						Plan changes and billing are handled on Shopify's secure plan selection page.
					</Text>
				</BlockStack>
				<Button
					variant="primary"
					size="large"
					url={planSelectionUrl ?? undefined}
					target="_top"
					disabled={!planSelectionUrl}
				>
					{actionLabel}
				</Button>
			</InlineStack>
		</Card>
	);
}

export default function Plans() {
	const [usage, setUsage] = useState<UsageData | null>(null);
	const [loading, setLoading] = useState(true);

	const shop = getShopDomain();
	const storeHandle = shop?.replace(".myshopify.com", "") ?? "";
	const planSelectionUrl = storeHandle
		? `https://admin.shopify.com/store/${storeHandle}/charges/${APP_HANDLE}/pricing_plans`
		: null;

	const loadUsage = useCallback(async () => {
		try {
			// Sync billing status from Shopify first, then load usage
			const billing = await authenticatedFetch<{ active: boolean; plan: string | null }>("/api/billing/status");
			const data = await authenticatedFetch<UsageData>("/api/usage");
			// If Shopify has an active plan, prefer that over DynamoDB
			if (billing.active && billing.plan) {
				data.planName = billing.plan;
			}
			setUsage(data);
		} catch (err) {
			console.error("Failed to load usage data:", err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadUsage();
	}, [loadUsage]);

	if (loading) {
		return (
			<Page title="Plans" subtitle="Review your current plan and compare available options">
				<Layout>
					<Layout.Section>
						<Card>
							<BlockStack gap="300">
								<SkeletonDisplayText size="small" />
								<SkeletonBodyText lines={2} />
							</BlockStack>
						</Card>
					</Layout.Section>
					<Layout.Section>
						<InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
							{PLANS.map((plan) => (
								<Card key={plan.name}>
									<BlockStack gap="400">
										<SkeletonDisplayText size="medium" />
										<SkeletonBodyText lines={6} />
									</BlockStack>
								</Card>
							))}
						</InlineGrid>
					</Layout.Section>
				</Layout>
			</Page>
		);
	}

	return (
		<Page title="Plans" subtitle="Review your current plan and compare available options">
			<Layout>
				<Layout.Section>
					<PlanManagementCard usage={usage} planSelectionUrl={planSelectionUrl} />
				</Layout.Section>
				{usage && (
					<Layout.Section>
						<UsageBar usage={usage} />
					</Layout.Section>
				)}
				<Layout.Section>
					<InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
						{PLANS.map((plan) => (
							<PlanCard
								key={plan.name}
								plan={plan}
								currentPlan={usage?.planName ?? ""}
							/>
						))}
					</InlineGrid>
				</Layout.Section>
				<Layout.Section>
					<Card>
						<BlockStack gap="300">
							<Text as="h2" variant="headingMd">
								Need a full shop translation?
							</Text>
							<Text as="p" variant="bodyMd" tone="subdued">
								We offer one-time complete translations of your entire product catalog.
								Get in touch and we'll handle everything for you.
							</Text>
							<Box>
								<Button url="mailto:support@duxly.nl" variant="primary">
									Contact us
								</Button>
							</Box>
						</BlockStack>
					</Card>
				</Layout.Section>
			</Layout>
		</Page>
	);
}
